import React from 'react';
import { useLocation } from 'react-router-dom';
import type { BaseBreadcrumbProps } from '@components';
import { SeparatorIconWrapper } from '@components/breadcrumb/styled.tsx';
import { DATE_FORMATS } from '@constants/date.ts';
import { useAppSelector } from '@hooks/reduxHooks.ts';
import { useCustomNavigate } from '@hooks/useCustomNavigate.ts';
import { DATA_FINAL_QUIZ_DISABLE } from '@hooks/useFinalQuizAbortGuard.ts';
import { useUserRoles } from '@hooks/useUserRoles.ts';
import { ROUTES } from '@router/constants.ts';
import { getAdminCenterContextFromToken } from '@routes/auth/___shared___/helpers.ts';
import { USER_DETAILS_PARAMS } from '@routes/profile-bar/___shared___/constants.ts';
import { TABS_CONS } from '@routes/sidebar/admin-center/___shared___/constants.ts';
import type { ClientDetailsResponse } from '@shared/api/clientDetail.api.ts';
import { readLockUserEmail, readLockUserTime } from '@store/services/localStorage.service.ts';
import { getAdminLandingPath } from '@utils/roles.ts';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';

export function isTemporarilyLocked(attemptedEmail: string): boolean {
    const lockEnd = readLockUserTime();
    const lockEmail = readLockUserEmail();

    if (!lockEnd || !lockEmail) {
        return false;
    }

    const lockExpiryDate = new Date(lockEnd);
    const now = new Date();

    return attemptedEmail === lockEmail && lockExpiryDate > now;
}

interface PathMap {
    [segment: string]: string;
}

interface GenOptions {
    pathMap?: PathMap;
}

type BreadcrumbItem = NonNullable<BaseBreadcrumbProps['items']>[number];

const getBreadcrumbLabel = (raw: string, pathMap: PathMap) =>
    (pathMap[raw] || raw)
        .split('-')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');

const getAdminConfigFlow = (
    segments: string[],
    pathMap: PathMap,
    homeBreadcrumb: BreadcrumbItem,
    adminBreadcrumb: BreadcrumbItem,
): BaseBreadcrumbProps['items'] => {
    const id = segments[2];
    const step = segments[3];
    const stepLabel = getBreadcrumbLabel(step, pathMap);

    return [
        homeBreadcrumb,
        adminBreadcrumb,
        {
            key: `admin-configuration-${id}-${step}`,
            title: `Configuration - ${stepLabel}`,
        },
    ];
};

const getAdminTabFlow = (
    segments: string[],
    pathMap: PathMap,
    homeBreadcrumb: BreadcrumbItem,
    adminBreadcrumb: BreadcrumbItem,
): BaseBreadcrumbProps['items'] => {
    const tab = segments[1];
    const tabLabel = getBreadcrumbLabel(tab, pathMap);

    return [
        homeBreadcrumb,
        adminBreadcrumb,
        {
            key: `admin-tab-${tab}`,
            title: tabLabel,
        },
    ];
};

const getDefaultFlow = (
    segments: string[],
    pathMap: PathMap,
    renderWrapper: (path?: string, label?: string) => React.ReactNode,
    homePath: string,
): BaseBreadcrumbProps['items'] => {
    const items: BaseBreadcrumbProps['items'] = [{ key: 'home', title: renderWrapper(homePath, 'Home') }];

    segments.forEach((seg, idx) => {
        const path = segments.slice(0, idx + 1).join('/');
        const label = getBreadcrumbLabel(seg, pathMap);

        items.push({
            key: path,
            title: idx < segments.length - 1 || seg === ROUTES.SIDEBAR.LEARNING ? renderWrapper(path, label) : label,
        });
    });

    return items;
};

export function useBreadcrumbItems(options: GenOptions = {}): BaseBreadcrumbProps['items'] {
    const { pathMap = {} } = options;
    const { pathname } = useLocation();
    const navigate = useCustomNavigate();
    const userRoles = useUserRoles();

    const selectedPathway = useAppSelector((state) => state.adminCenterCreatorPathways.selectedPathway);
    const selectedElement = useAppSelector((state) => state.adminCenterCreatorElements.selectedElement);
    const selectedVessel = useAppSelector((state) => state.adminCenterFleetVessel.selectedVessel);
    const selectedVesselCrewMember = useAppSelector(
        (state) => state.adminCenterVesselCrewMember.selectedVesselCrewMember,
    );
    const assessorTaskData = useAppSelector((state) => state.assessorEvaluateTask.taskData);
    const userInfo = useAppSelector((state) => state.user.userInfo);
    const landingPage = userInfo?.preferences?.landingPage || ROUTES.SIDEBAR.LEARNING;

    const adminBreadcrumbPath = getAdminLandingPath({ userRoles, ...getAdminCenterContextFromToken() });

    // 1. Strip leading/trailing slashes
    // 2. Split into ["foo", "bar", ...]
    // 3. Remove any empty strings (in case of "//" or root path)
    const segments = pathname
        .replace(/^\/|\/$/g, '')
        .split('/')
        .filter(Boolean);

    const handleNavigate = (to: string) => (e: React.MouseEvent | React.KeyboardEvent) => {
        e.preventDefault();
        navigate(to);
    };

    const renderWrapper = (path = '', label = '') => (
        <span className="crumbs" {...DATA_FINAL_QUIZ_DISABLE}>
            <span
                onClick={handleNavigate(path)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        handleNavigate(path)(e);
                    }
                }}
            >
                {label}
            </span>
            <SeparatorIconWrapper />
        </span>
    );

    const HOME_BREADCRUMB = {
        key: 'home',
        title: renderWrapper(`/${landingPage}`, 'Home'),
    };
    const ADMIN_BREADCRUMB = {
        key: 'admin',
        title: renderWrapper(adminBreadcrumbPath, 'Admin'),
    };
    const ADMIN_CREATOR_BREADCRUMB = {
        key: 'admin-creator',
        title: renderWrapper(`${ROUTES.SIDEBAR.ADMIN.ROOT}/${ROUTES.SIDEBAR.ADMIN.CREATOR}`, 'Creator'),
    };
    const ADMIN_CREATOR_PATHWAYS_BREADCRUMB = {
        key: 'admin-creator-pathways',
        title: renderWrapper(
            `${ROUTES.SIDEBAR.ADMIN.ROOT}/${ROUTES.SIDEBAR.ADMIN.CREATOR}/${ROUTES.SIDEBAR.ADMIN.CREATOR_PATHWAYS.ROOT}`,
            'Pathways',
        ),
    };
    const ADMIN_CREATOR_PATHWAYS_EDIT_BREADCRUMB = {
        key: 'creator-pathway-edit',
        title: selectedPathway?.title || 'Edit Pathway',
    };
    const ADMIN_CREATOR_PATHWAYS_CREATE_BREADCRUMB = {
        key: 'creator-pathway-create',
        title: 'Create new pathway',
    };
    const ADMIN_CREATOR_ELEMENTS_BREADCRUMB = {
        key: 'admin-creator-elements',
        title: renderWrapper(
            `${ROUTES.SIDEBAR.ADMIN.ROOT}/${ROUTES.SIDEBAR.ADMIN.CREATOR}/${ROUTES.SIDEBAR.ADMIN.CREATOR_ELEMENTS.ROOT}`,
            'Elements',
        ),
    };
    const ADMIN_CREATOR_ELEMENTS_EDIT_BREADCRUMB = {
        key: 'creator-element-edit',
        title: selectedElement?.title || 'Edit Element',
    };
    const ADMIN_CREATOR_ELEMENTS_CREATE_BREADCRUMB = {
        key: 'creator-element-create',
        title: 'Create new element',
    };
    const ADMIN_FLEET_DASHBOARD_BREADCRUMB = {
        key: 'fleet-dashboard',
        title: renderWrapper(`${ROUTES.SIDEBAR.ADMIN.ROOT}/${ROUTES.SIDEBAR.ADMIN.FLEET_DASHBOARD}`, 'Fleet Dashboard'),
    };
    const ADMIN_FLEET_DASHBOARD_VESSEL_BREADCRUMB = {
        key: 'fleet-dashboard-vessel',
        title: renderWrapper(
            `${ROUTES.SIDEBAR.ADMIN.ROOT}/${ROUTES.SIDEBAR.ADMIN.FLEET_DASHBOARD}/${ROUTES.SIDEBAR.ADMIN.FLEET_VESSEL}/${segments[3]}`,
            selectedVessel?.name || '',
        ),
    };
    const ADMIN_FLEET_DASHBOARD_CREW_MEMBER_BREADCRUMB = {
        key: 'fleet-dashboard-vessel-crew-member',
        title: selectedVesselCrewMember?.userName || '',
    };
    const ADMIN_VESSEL_DASHBOARD_BREADCRUMB = {
        key: 'vessel-dashboard',
        title: renderWrapper(
            `${ROUTES.SIDEBAR.ADMIN.ROOT}/${ROUTES.SIDEBAR.ADMIN.VESSEL_DASHBOARD}`,
            'Vessel Dashboard',
        ),
    };
    const ADMIN_VESSEL_DASHBOARD_CREW_MEMBER_BREADCRUMB = {
        key: 'vessel-dashboard-crew-member',
        title: selectedVesselCrewMember?.userName || '',
    };

    const ADMIN_ASSESSOR_BREADCRUMB = {
        key: 'admin-assessor',
        title: renderWrapper(`${ROUTES.SIDEBAR.ADMIN.ROOT}/${ROUTES.SIDEBAR.ADMIN.ASSESSOR.ROOT}`, 'Assessor'),
    };
    const ADMIN_ASSESSOR_TASK_BREADCRUMB = {
        key: 'admin-assessor-task',
        title: (() => {
            const assesseeName = assessorTaskData?.details?.name ?? '';
            const assesseeRank = assessorTaskData?.details?.rankName ?? '';
            const taskName = assessorTaskData?.details?.taskName ?? '';
            const assesseeLabel = [assesseeRank, assesseeName].filter(Boolean).join(' ');

            if (!assesseeLabel && !taskName) {
                return 'Task evaluation';
            }

            return `${assesseeLabel}${taskName ? ` – ${taskName}` : ''}`;
        })(),
    };
    const LEARNING_BREADCRUMB = {
        key: 'learning',
        title: (
            <span className="crumbs" {...DATA_FINAL_QUIZ_DISABLE}>
                <span
                    onClick={() =>
                        navigate(ROUTES.SIDEBAR.LEARNING, {
                            state: { resetLearningTab: true } as unknown as { from: string },
                        })
                    }
                >
                    Learning
                </span>
                <SeparatorIconWrapper />
            </span>
        ),
    };

    const ADMIN_TABS = new Set(Object.values(TABS_CONS));
    const ADMIN_CREATOR_PATHWAYS_BREADCRUMB_PATH = [
        HOME_BREADCRUMB,
        ADMIN_BREADCRUMB,
        ADMIN_CREATOR_BREADCRUMB,
        ADMIN_CREATOR_PATHWAYS_BREADCRUMB,
    ];
    const ADMIN_CREATOR_ELEMENTS_BREADCRUMB_PATH = [
        HOME_BREADCRUMB,
        ADMIN_BREADCRUMB,
        ADMIN_CREATOR_BREADCRUMB,
        ADMIN_CREATOR_ELEMENTS_BREADCRUMB,
    ];
    const ADMIN_FLEET_DASHBOARD_BREADCRUMB_PATH = [HOME_BREADCRUMB, ADMIN_BREADCRUMB, ADMIN_FLEET_DASHBOARD_BREADCRUMB];
    const ADMIN_VESSEL_DASHBOARD_BREADCRUMB_PATH = [
        HOME_BREADCRUMB,
        ADMIN_BREADCRUMB,
        ADMIN_VESSEL_DASHBOARD_BREADCRUMB,
    ];
    const ADMIN_ASSESSOR_BREADCRUMB_PATH = [HOME_BREADCRUMB, ADMIN_BREADCRUMB, ADMIN_ASSESSOR_BREADCRUMB];

    const isAdminConfigFlow =
        segments[0] === 'admin' && segments[1] === TABS_CONS.CONFIGURATION && !!segments[2] && !!segments[3];
    const isAdminTabFlow = segments[0] === 'admin' && !!segments[1] && ADMIN_TABS.has(segments[1]);

    const isCreatorPathwaysRoot =
        segments[0] === ROUTES.SIDEBAR.ADMIN.ROOT &&
        segments[1] === ROUTES.SIDEBAR.ADMIN.CREATOR &&
        segments[2] === ROUTES.SIDEBAR.ADMIN.CREATOR_PATHWAYS.ROOT;

    const isFleetDashboard =
        segments[0] === ROUTES.SIDEBAR.ADMIN.ROOT && segments[1] === ROUTES.SIDEBAR.ADMIN.FLEET_DASHBOARD;

    const isFleetDashboardVessel =
        segments[0] === ROUTES.SIDEBAR.ADMIN.ROOT &&
        segments[1] === ROUTES.SIDEBAR.ADMIN.FLEET_DASHBOARD &&
        segments[2] === ROUTES.SIDEBAR.ADMIN.FLEET_VESSEL &&
        !!segments[3] &&
        !segments[4];

    const isFleetDashboardVesselCrewMember =
        segments[0] === ROUTES.SIDEBAR.ADMIN.ROOT &&
        segments[1] === ROUTES.SIDEBAR.ADMIN.FLEET_DASHBOARD &&
        segments[2] === ROUTES.SIDEBAR.ADMIN.FLEET_VESSEL &&
        !!segments[3] &&
        segments[4] === ROUTES.SIDEBAR.ADMIN.VESSEL_CREW_MEMBER &&
        !!segments[5];

    const isVesselDashboard =
        segments[0] === ROUTES.SIDEBAR.ADMIN.ROOT && segments[1] === ROUTES.SIDEBAR.ADMIN.VESSEL_DASHBOARD;

    const isVesselDashboardCrewMember =
        segments[0] === ROUTES.SIDEBAR.ADMIN.ROOT &&
        segments[1] === ROUTES.SIDEBAR.ADMIN.VESSEL_DASHBOARD &&
        segments[2] === ROUTES.SIDEBAR.ADMIN.VESSEL_CREW_MEMBER &&
        !!segments[3];

    const isCreatorPathwayEditFlow =
        isCreatorPathwaysRoot && segments[3] === ROUTES.SIDEBAR.ADMIN.CREATOR_PATHWAYS.EDIT;

    const isCreatorPathwayCreateFlow =
        isCreatorPathwaysRoot && segments[3] === ROUTES.SIDEBAR.ADMIN.CREATOR_PATHWAYS.CREATE;

    const isCreatorElementsRoot =
        segments[0] === ROUTES.SIDEBAR.ADMIN.ROOT &&
        segments[1] === ROUTES.SIDEBAR.ADMIN.CREATOR &&
        segments[2] === ROUTES.SIDEBAR.ADMIN.CREATOR_ELEMENTS.ROOT;

    const isCreatorElementEditFlow =
        isCreatorElementsRoot && segments[3] === ROUTES.SIDEBAR.ADMIN.CREATOR_ELEMENTS.EDIT;

    const isCreatorElementCreateFlow =
        isCreatorElementsRoot && segments[3] === ROUTES.SIDEBAR.ADMIN.CREATOR_ELEMENTS.CREATE;

    const isAssessorEvaluateTaskFlow =
        segments[0] === ROUTES.SIDEBAR.ADMIN.ROOT &&
        segments[1] === ROUTES.SIDEBAR.ADMIN.ASSESSOR.ROOT &&
        segments[2] === ROUTES.SIDEBAR.ADMIN.ASSESSOR.TASK &&
        !!segments[3] &&
        !!segments[4];

    const isLearningRoot = pathname.replace(/^\/|\/$/g, '') === ROUTES.SIDEBAR.LEARNING.replace(/^\/|\/$/g, '');

    switch (true) {
        case isLearningRoot:
            return [HOME_BREADCRUMB, LEARNING_BREADCRUMB];

        case isAdminConfigFlow:
            return getAdminConfigFlow(segments, pathMap, HOME_BREADCRUMB, ADMIN_BREADCRUMB);

        case isAssessorEvaluateTaskFlow:
            return [...ADMIN_ASSESSOR_BREADCRUMB_PATH, ADMIN_ASSESSOR_TASK_BREADCRUMB];

        case isAdminTabFlow:
            return getAdminTabFlow(segments, pathMap, HOME_BREADCRUMB, ADMIN_BREADCRUMB);

        case isCreatorPathwayEditFlow:
            return [...ADMIN_CREATOR_PATHWAYS_BREADCRUMB_PATH, ADMIN_CREATOR_PATHWAYS_EDIT_BREADCRUMB];

        case isCreatorPathwayCreateFlow:
            return [...ADMIN_CREATOR_PATHWAYS_BREADCRUMB_PATH, ADMIN_CREATOR_PATHWAYS_CREATE_BREADCRUMB];

        case isCreatorElementEditFlow:
            return [...ADMIN_CREATOR_ELEMENTS_BREADCRUMB_PATH, ADMIN_CREATOR_ELEMENTS_EDIT_BREADCRUMB];

        case isCreatorElementCreateFlow:
            return [...ADMIN_CREATOR_ELEMENTS_BREADCRUMB_PATH, ADMIN_CREATOR_ELEMENTS_CREATE_BREADCRUMB];

        case isFleetDashboardVesselCrewMember:
            return [
                ...ADMIN_FLEET_DASHBOARD_BREADCRUMB_PATH,
                ADMIN_FLEET_DASHBOARD_VESSEL_BREADCRUMB,
                ADMIN_FLEET_DASHBOARD_CREW_MEMBER_BREADCRUMB,
            ];

        case isFleetDashboardVessel:
            return [...ADMIN_FLEET_DASHBOARD_BREADCRUMB_PATH, ADMIN_FLEET_DASHBOARD_VESSEL_BREADCRUMB];

        case isFleetDashboard:
            return [...ADMIN_FLEET_DASHBOARD_BREADCRUMB_PATH];

        case isVesselDashboardCrewMember:
            return [...ADMIN_VESSEL_DASHBOARD_BREADCRUMB_PATH, ADMIN_VESSEL_DASHBOARD_CREW_MEMBER_BREADCRUMB];

        case isVesselDashboard:
            return [...ADMIN_VESSEL_DASHBOARD_BREADCRUMB_PATH];

        default:
            return getDefaultFlow(segments, pathMap, renderWrapper, `/${landingPage}`);
    }
}

export const renderAlertErrorDesc = (status: string, text: string) => {
    return `status: ${status}, description: ${text}.`;
};

export const getChangedObjectKeys = <T extends Record<string, any>>(original: T, updated: T): Partial<T> => {
    return Object.keys(original).reduce((acc, key) => {
        const typedKey = key as keyof T;

        if (key in updated && !isEqual(original[typedKey], updated[typedKey])) {
            acc[typedKey] = updated[typedKey];
        }

        return acc;
    }, {} as Partial<T>);
};

export const normalizeDateFieldsForPersistence = (
    modifiedFields: Record<string, any>,
    userInfo: Record<string, any>,
    clientDetails: ClientDetailsResponse | null,
): Record<string, any> => {
    const result: Record<string, any> = {};

    Object.entries(modifiedFields).forEach(([key, value]) => {
        const normalizedKey = key.toLowerCase();

        if (normalizedKey === USER_DETAILS_PARAMS.GENDER) {
            result[key] = clientDetails?.genders?.find((gender) => gender.id === value)?.type;

            return;
        }

        if (normalizedKey === USER_DETAILS_PARAMS.NATIONALITY) {
            result[USER_DETAILS_PARAMS.COUNTRY_ID] = clientDetails?.nationalities?.find(
                (nationality) => nationality.isoAlhpa2 === value,
            )?.id;

            return;
        }

        if (normalizedKey.endsWith('utc')) {
            const modifiedDate = dayjs(value).format(DATE_FORMATS.ISO);
            const originalDate = dayjs(userInfo[key]).format(DATE_FORMATS.ISO);

            if (!isEqual(modifiedDate, originalDate)) {
                result[key] = modifiedDate;
            }

            return;
        }

        result[key] = value;
    });

    return result;
};

// this func helps to find html parse errors
export const findHtmlError = (html: string): { message: string; snippet: string } | null => {
    const tagRe = /<\/?([A-Za-z][A-Za-z0-9]*)\b[^>]*>/g;
    const selfClosing = new Set([
        'area',
        'base',
        'br',
        'col',
        'embed',
        'hr',
        'img',
        'input',
        'link',
        'meta',
        'param',
        'source',
        'track',
        'wbr',
    ]);

    const stack: { tag: string; index: number }[] = [];

    for (const match of html.matchAll(tagRe)) {
        const rawTag = match[0];
        const tagName = match[1];
        const idx = match.index!;

        const isClosing = rawTag.startsWith('</');
        const isSelfClosing = rawTag.endsWith('/>') || selfClosing.has(tagName.toLowerCase());

        if (!isClosing && !isSelfClosing) {
            stack.push({ tag: tagName, index: idx });
        } else if (isClosing) {
            const top = stack[stack.length - 1];
            if (top && top.tag.toLowerCase() === tagName.toLowerCase()) {
                stack.pop();
            } else {
                return {
                    message: `Unexpected closing tag </${tagName}>`,
                    snippet: rawTag,
                };
            }
        }
    }

    if (stack.length > 0) {
        const unclosed = stack[stack.length - 1];
        const rest = html.slice(unclosed.index);
        const endOfTag = rest.indexOf('>') + 1;
        return {
            message: `Unclosed tag <${unclosed.tag}>`,
            snippet: html.slice(unclosed.index, unclosed.index + endOfTag),
        };
    }

    return null;
};

export const findOptionByValue = <T extends { value: any }>(options: T[], value?: T['value']): T | '' =>
    options.find((option) => option.value === value) ?? '';

export const toDataCyValue = (value: string): string => {
    return value.trim().toLowerCase().replace(/\s+/g, '-');
};
